/**
 * Site Subscription Hook (single site)
 *
 * TanStack Query-backed hook for per-site subscription state. Backed by
 * `['sites', siteId, 'subscription']` — the multi-site hook
 * (`useSiteSubscriptions`) uses `useQueries` against the same key so the
 * cache is shared across both call sites.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/auth0-provider';
import { getSiteSubscription } from '@/api/billingApi';
import type { SubscriptionTier } from '@/types/billing';

export interface SiteSubscription {
  tier: SubscriptionTier | 'free';
  paid?: boolean;
  status: string;
  amount: number;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  plan?: {
    id: string;
    name: string;
    monthlyPrice: number;
  };
}

/**
 * Fetch and shape a single site's subscription. Returns `null` when the API
 * call fails or returns no data — matches the previous AppCache behavior so
 * consumers can keep using truthiness checks. Exported for reuse by
 * `useSiteSubscriptions` so both hooks run identical fetch logic.
 */
export async function fetchSiteSubscription(siteId: string): Promise<SiteSubscription | null> {
  let d;
  try {
    d = await getSiteSubscription(siteId);
  } catch {
    return null;
  }
  return {
    tier: d.tier,
    paid: d.paid,
    status: d.status,
    amount: d.amount,
    currentPeriodEnd: d.currentPeriodEnd,
    cancelAtPeriodEnd: d.cancelAtPeriodEnd,
    plan: d.plan ?? undefined,
  };
}

/**
 * Hook for fetching a single site's subscription. Backed by
 * `['sites', siteId, 'subscription']`. Gated on auth readiness so the hook
 * does not fire during the cold-load window (mirrors `useSite`).
 */
export function useSiteSubscription(siteId: string | undefined) {
  const { isAuthenticated, isSdkReady } = useAuth();

  return useQuery<SiteSubscription | null>({
    queryKey: ['sites', siteId, 'subscription'],
    queryFn: () => fetchSiteSubscription(siteId!),
    enabled: !!siteId && isAuthenticated && isSdkReady,
  });
}
